/**
 * The one site header.
 *
 * It was previously copy-pasted into seven page files, which is how the two
 * variants got out of step: the homepage said "Explore Resource Center" in the
 * body sans, the Resource Center said "Tools & Services" in italic serif, and
 * the fifteen /resources/<tool> pages rendered no header at all. Every route
 * uses this component now.
 *
 * Three rules it exists to hold:
 *
 *   1. One wordmark. "Your Smart Stack" is the brand. "The AI Roles Map" is
 *      the name of this database and its quiz, so it rides in the context slot
 *      rather than competing for the wordmark.
 *   2. One context label style. Uppercase, tracked, muted, after a hairline.
 *      Never italic serif on one route and sans on another.
 *   3. The primary action points at the quiz, not at an account. "Find my
 *      tools" is what a first-time visitor wants; "Log in" is for the small
 *      minority who already have an account, so it sits back as a text link.
 *   4. The way back is part of the header, not a band underneath it. It was a
 *      separate row below the sticky bar, which meant it scrolled away: on a
 *      long guide — and the guides are the longest pages on the site — the
 *      control for "take me back to where I came from" was reachable only by
 *      scrolling to the top first. Inside the sticky element it is always
 *      there. This also makes it the one place a back link is described, so
 *      pages stop each inventing their own position for it.
 */

import { Link, useLocation } from "wouter";
import { ArrowLeft, LayoutDashboard } from "lucide-react";
import { BUTTON } from "@/lib/buttonStyles";
import AuthControl from "@/components/AuthControl";
import { useAuth } from "@/core/hooks/useAuth";
import { trpc } from "@/lib/trpc";

interface SiteHeaderProps {
  /**
   * The uppercase label after the wordmark. Defaults to the database's own
   * name because most routes are part of it.
   */
  context?: string;
  /**
   * Hides the "Find my tools" button on routes where it would be noise, such
   * as the login and password reset pages.
   */
  showPrimaryAction?: boolean;
  /**
   * One step back, rendered as a second row inside the sticky header. Both
   * halves are required — a href with no label is a control with no name, and
   * a label with no href is decoration.
   *
   * Deliberately not defaulted to "/". The wordmark already goes home; this
   * row exists to go back *one* step, to the tool page or guide index the
   * visitor actually came from, and guessing that wrong is worse than leaving
   * it off.
   */
  backHref?: string;
  backLabel?: string;
}

export default function SiteHeader({
  context = "The AI Roles Map",
  showPrimaryAction = true,
  backHref,
  backLabel,
}: SiteHeaderProps) {
  const [location, setLocation] = useLocation();
  const { isAuthenticated, user } = useAuth();
  const onHome = location === "/";
  /**
   * The link is rendered for admins only.
   *
   * Not a security measure — /admin is guarded on the server and would refuse
   * anyone else. It is about not advertising a door that almost nobody can
   * open, in a header whose whole design note above is that it should point a
   * first-time visitor at the quiz.
   */
  const isAdmin = user?.role === "admin";

  /* Only asked once somebody is signed in — an anonymous visitor is never a
     Partner, and the header renders on every page, so this must not be a
     round trip for the whole internet. Answers null for the great majority
     of signed-in people, who are customers. */
  const partner = trpc.partner.status.useQuery(undefined, { enabled: isAuthenticated });
  const isPartner = !!partner.data;

  /**
   * On the homepage the quiz is already on screen, so this scrolls back to it
   * rather than triggering a navigation that would look like nothing happened.
   */
  const findMyTools = () => {
    if (onHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setLocation("/");
    }
  };

  return (
    <header className="border-b border-border bg-background/90 backdrop-blur-sm sticky top-0 z-50">
      {/* gap-2 on a phone, not gap-5: at 390px the wordmark, "Log in" and the
          primary button each wrapped onto two lines, tripling the header
          height. whitespace-nowrap on every item is what actually holds it;
          the gaps only decide how much room is left over. */}
      <div className="container flex items-center justify-between gap-2 sm:gap-4 h-16">
        <Link
          href="/"
          className="flex items-center gap-3 min-w-0 hover:opacity-75 transition-opacity"
        >
          <span className="font-['Fraunces'] text-lg sm:text-2xl font-bold text-foreground tracking-tight whitespace-nowrap">
            Your Smart Stack
          </span>
          <span className="hidden lg:block text-xs text-muted-foreground font-medium tracking-widest uppercase border-l border-border pl-3 whitespace-nowrap">
            {context}
          </span>
        </Link>

        <div className="flex items-center gap-3 sm:gap-5 shrink-0">
          <Link
            href="/resources"
            className="hidden md:inline-flex items-center text-sm font-medium text-foreground/70 hover:text-foreground transition-colors whitespace-nowrap"
          >
            Resource Center
          </Link>

          <Link
            href="/prompts"
            className="hidden md:inline-flex items-center text-sm font-medium text-foreground/70 hover:text-foreground transition-colors whitespace-nowrap"
          >
            Prompt Library
          </Link>

          {/* A sister site, so a plain anchor: a wouter Link would treat it as
              a route in this app. Same tab, like the storefront's links here. */}
          <a
            href="https://videos.yoursmartstack.com/"
            className="hidden md:inline-flex items-center text-sm font-medium text-foreground/70 hover:text-foreground transition-colors whitespace-nowrap"
          >
            Videos
          </a>

          {isPartner ? (
            <Link
              href="/partner"
              className="hidden md:inline-flex items-center text-sm font-medium text-foreground/70 hover:text-foreground transition-colors whitespace-nowrap"
            >
              Partner Dashboard
            </Link>
          ) : null}

          {isAdmin ? (
            <Link
              href="/admin"
              className="hidden md:inline-flex items-center text-sm font-medium text-foreground/70 hover:text-foreground transition-colors whitespace-nowrap"
            >
              Admin
            </Link>
          ) : null}

          <AuthControl />

          {showPrimaryAction ? (
            isAuthenticated ? (
              /* Nov 2026: the dashboard is a member's home now; My Stack is
                 one click from it. */
              <Link
                href="/dashboard"
                aria-label="Dashboard"
                className={`whitespace-nowrap ${BUTTON.primarySm}`}
              >
                {/* Icon only below 420px: "Dashboard" is wider than the
                    "My Stack" it replaced and pushed the wordmark off the
                    edge on a 375px phone, next to "Log out". */}
                <LayoutDashboard className="h-4 w-4 min-[420px]:hidden" aria-hidden="true" />
                <span className="hidden min-[420px]:inline">Dashboard</span>
              </Link>
            ) : (
              <button
                onClick={findMyTools}
                className={`whitespace-nowrap ${BUTTON.primarySm}`}
              >
                {/* "Find my tools" is 13 characters and does not fit beside the
                    wordmark on a 390px screen. */}
                <span className="sm:hidden">Find tools</span>
                <span className="hidden sm:inline">Find my tools</span>
              </button>
            )
          ) : null}
        </div>
      </div>

      {/* Inside <header>, so it sticks with it rather than scrolling out from
          under it. Its own hairline and tint keep it legible as a separate
          band — the arrangement people already know — while being one element
          as far as the browser is concerned. */}
      {backHref && backLabel ? (
        <div className="border-t border-border bg-muted/40">
          <div className="container">
            <Link
              href={backHref}
              className="inline-flex items-center gap-1.5 min-h-11 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              {backLabel}
            </Link>
          </div>
        </div>
      ) : null}
    </header>
  );
}
